import toArray from "lodash/toArray";

import { Fragment } from "react";

import { Flex, Stack } from "@/storybook-components";
import CheckBoxQuestion from "./checkbox-question";
import DateQuestion from "./date-question";
import DropDownQuestion from "./drop-down-question";
import EmailQuestion from "./email-question";
import EmojiQuestion from "./emoji-question";
import EssayQuestion from "./essay-question";
import FileQuestion from "./file-question";
import LikertQuestion from "./likert-question";
import NumberQuestion from "./number-question";
import RadioButtonQuestion from "./radio-button-question";
import RankingQuestion from "./ranking-question";
import RegionQuestion from "./region-question";
import SectionQuestion from "./section-question";
import SingleLineQuestion from "./single-line-question";
import { segregateQuestions, triggeredQuestions } from "@/helpers/survey";

import type { Question } from "@/types";
import { useAppSelector } from "@/redux/store/hooks";
import { surveyParticipantFieldContainer } from "./survey-participant-test-ids";

type ListProps = {
  parentQuestions: Question[];
  conditionalQuestions: Record<string, Question[]>;
  /** If set, field ids are `${nestedUnderFieldId}-${childIndex}` (conditional questions). */
  nestedUnderFieldId?: string;
};

const RenderQuestionList = ({
  parentQuestions,
  conditionalQuestions,
  nestedUnderFieldId
}: ListProps) => {
  const { answers } = useAppSelector((state) => state.survey);

  const questionNode = (question: Question, participantFieldId: string) => {
    const fieldProps = { participantFieldId };
    switch (question.type) {
      case "DateQuestion":
        return <DateQuestion question={question} {...fieldProps} />;
      case "FileQuestion":
        return <FileQuestion question={question} {...fieldProps} />;
      case "EmojiQuestion":
        return <EmojiQuestion question={question} {...fieldProps} />;
      case "EmailQuestion":
        return <EmailQuestion question={question} {...fieldProps} />;
      case "EssayQuestion":
        return <EssayQuestion question={question} {...fieldProps} />;
      case "NumberQuestion":
        return <NumberQuestion question={question} {...fieldProps} />;
      case "LikertQuestion":
        return <LikertQuestion question={question} {...fieldProps} />;
      case "RegionQuestion":
        return <RegionQuestion question={question} {...fieldProps} />;
      case "RankingQuestion":
        return <RankingQuestion question={question} {...fieldProps} />;
      case "SectionQuestion":
        return <SectionQuestion question={question} {...fieldProps} />;
      case "SingleLineQuestion":
        return <SingleLineQuestion question={question} {...fieldProps} />;
      case "CheckBoxQuestion":
        return <CheckBoxQuestion question={question} {...fieldProps} />;
      case "DropDownQuestion":
        return <DropDownQuestion question={question} {...fieldProps} />;
      case "RadioButtonQuestion":
        return <RadioButtonQuestion question={question} {...fieldProps} />;
      default:
        return null;
    }
  };

  const findTriggeredQuestions = (
    parent: Question,
    children: Array<Question>,
    parentFieldId: string
  ) => {
    if (!answers[parent.id]) return null;

    const triggered = triggeredQuestions(answers[parent.id], children);

    return (
      <RenderQuestionList
        parentQuestions={triggered}
        conditionalQuestions={conditionalQuestions}
        nestedUnderFieldId={parentFieldId}
      />
    );
  };

  return parentQuestions.map((question: Question, index: number) => {
    const participantFieldId =
      nestedUnderFieldId === undefined
        ? String(index)
        : `${nestedUnderFieldId}-${index}`;

    return (
      <Fragment key={`ehq-survey-${question.id}`}>
        {question.type === "SectionQuestion" ? (
          questionNode(question, participantFieldId)
        ) : (
          <Flex
            data-testid={surveyParticipantFieldContainer(participantFieldId)}
          >
            {questionNode(question, participantFieldId)}
          </Flex>
        )}
        {!!conditionalQuestions[question.id] &&
          findTriggeredQuestions(
            question,
            conditionalQuestions[question.id],
            participantFieldId
          )}
      </Fragment>
    );
  });
};

const QuestionList = (): JSX.Element => {
  const { pages, questions, options } = useAppSelector((state) => state.survey);

  const currentPage = pages.find((page) => page.current);
  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage.id
  );
  const optionsInPage = toArray(options).filter((o) =>
    questionsInPage.some((q) => q.id === o.questionId)
  );

  const { parentQuestions, conditionalQuestions } = segregateQuestions(
    questionsInPage,
    optionsInPage
  );

  const state = {
    conditionalQuestions,
    parentQuestions: parentQuestions.filter((q) => !q.skip)
  };

  return (
    <Stack gap={12} data-testid="survey-participant-question-list">
      <RenderQuestionList {...state} />
    </Stack>
  );
};

export default QuestionList;
